<template>
  <div class="relative" ref="containerRef">
    <button
      @click="showDropdown = !showDropdown"
      ref="buttonRef"
      class="flex items-center gap-2 rounded-lg bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
    >
      <span>{{ currentLanguage.toUpperCase() }}</span>
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    
    <Transition name="fade">
      <div
        v-if="showDropdown"
        ref="dropdownRef"
        class="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border overflow-hidden z-50 min-w-[120px]"
      >
        <button
          v-for="lang in languages"
          :key="lang.code"
          @click="switchLanguage(lang.code)"
          :class="[
            'w-full text-left px-4 py-2 text-sm hover:bg-gray-100 transition',
            currentLanguage === lang.code ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'
          ]"
        >
          {{ lang.name }}
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { CONFIG } from '../../config';
import { SUPPORTED_LOCALES_WITH_NAMES } from '@merlot/common/localeConstants.js';
import { setLocaleCookie } from '../../utils/localeCookie.js';
import { mergeLocaleMessagesDeep } from '../../utils/mergeLocaleMessages.js';
import { publishedLocaleMessages, publishedTranslationFingerprints } from '../../i18n/publishedBundles.js';
import {
  syncPublishedLocaleMessagesIfNeeded,
  shouldUseRemoteTranslationCheck
} from '../../utils/i18nRemoteSync.js';

const { locale, setLocaleMessage, messages } = useI18n();
const route = useRoute();
const router = useRouter();

const showDropdown = ref(false);
const dropdownRef = ref(null);
const containerRef = ref(null);
const buttonRef = ref(null);

const languages = SUPPORTED_LOCALES_WITH_NAMES;

// Get current language from route param or i18n
const currentLanguage = computed(() => {
  return route.params.lang || locale.value || CONFIG.DEFAULT_LOCALE;
});

const switchLanguage = async (langCode) => {
  showDropdown.value = false;
  
  // Set cookie for nginx locale detection and persistence
  setLocaleCookie(langCode);
  
  // Check if current route has language prefix (SSG route)
  const currentLang = route.params.lang;
  
  if (currentLang && CONFIG.SUPPORTED_LOCALES.includes(currentLang)) {
    // SSG route: each locale is a separate pre-rendered HTML file under /{lang}/...
    // Full navigation loads that document (correct __INITIAL_STATE__ + SEO). With cache off,
    // the browser re-requests linked JS/CSS; with Cache-Control on /assets, those stay cached.
    const newPath = route.path.replace(`/${currentLang}`, `/${langCode}`);
    window.location.href = newPath;
  } else {
    const bundledFp = publishedTranslationFingerprints[langCode] || '';
    await syncPublishedLocaleMessagesIfNeeded({
      locale: langCode,
      fallbackMessages: publishedLocaleMessages,
      bundledFingerprint: bundledFp,
      setLocaleMessage
    });

    if (import.meta.env.DEV && !shouldUseRemoteTranslationCheck(bundledFp)) {
      try {
        const apiUrl = CONFIG.API_BASE_URL;
        const response = await fetch(`${apiUrl}/i18n/${langCode}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.translations) {
            const currentMessages = messages.value[langCode] || {};
            const merged = mergeLocaleMessagesDeep(
              JSON.parse(JSON.stringify(currentMessages)),
              data.translations
            );
            setLocaleMessage(langCode, merged);
          }
        }
      } catch (error) {
        console.warn(`Failed to fetch translations for ${langCode}:`, error.message);
      }
    }

    locale.value = langCode;
  }
};

// Handle click outside
const handleClickOutside = (event) => {
  if (containerRef.value && !containerRef.value.contains(event.target)) {
    showDropdown.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  
  // Sync i18n locale with route param on mount
  const routeLang = route.params.lang;
  if (routeLang && CONFIG.SUPPORTED_LOCALES.includes(routeLang)) {
    locale.value = routeLang;
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
</style>
